import * as analytic from '@anm/analytic';
import noop from '@anm/helpers/noop';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useIsWebinar from '../../../../hooks/useIsWebinar';

import { hostedVideoActions, hostedVideoSelectors } from '../../../../store/modules/hostedVideo';
import { landingsActions, landingsSelectors } from '../../../../store/modules/landings';
import { useCheckPoster } from '../../PosterBlock/hooks';

type ChangedData = 'configs' | 'poster' | 'landing' | 'password';

type Action = 'save' | 'discard';

export type ActionHandlers = {
  [key in Action]: () => void;
} & {
  isPending: boolean;
};

const {
  selectPoster,
  selectPlayerColor,
  selectVideoConfigs,
  selectVideoPassword,
  selectWasPosterChanged,
  selectWasConfigsChanged,
  selectSavePosterPending,
  selectSaveConfigsPending,
  selectWasPasswordChanged,
  selectSavePasswordPending
} = hostedVideoSelectors;

const { selectLanding, selectWasLandingChanged, selectSaveLandingPendingStatus } = landingsSelectors;

const {
  saveVideoConfigs,
  uploadCustomPoster,
  discardPosterChanges,
  discardConfigsChanges,
  discardPasswordChanges,
  discardPlayerColorChanges
} = hostedVideoActions;

const { discardLandingChanges } = landingsActions;

const useHandlers = (hostId: string) => {
  const configs = useSelector(selectVideoConfigs);
  const landing = useSelector(selectLanding);
  const poster = useSelector(selectPoster);
  const playerColor = useSelector(selectPlayerColor);
  const password = useSelector(selectVideoPassword);
  const isWebinar = useIsWebinar();

  const wasPosterChanged = useSelector(selectWasPosterChanged);
  const wasConfigsChanged = useSelector(selectWasConfigsChanged);
  const wasLandingChanged = useSelector(selectWasLandingChanged);
  const wasPasswordChanged = useSelector(selectWasPasswordChanged);

  const isPosterPending = useSelector(selectSavePosterPending);
  const isConfigsPending = useSelector(selectSaveConfigsPending);
  const isPasswordPending = useSelector(selectSavePasswordPending);
  const isLandingPending = useSelector(selectSaveLandingPendingStatus);

  const [isAppearancePending, setAppearancePending] = useState(false);
  const [isChecking, isPosterReady] = useCheckPoster(hostId);

  const wasPosterUpdated = wasPosterChanged || isChecking;

  const wasChanged = wasPosterUpdated || wasConfigsChanged || wasLandingChanged || wasPasswordChanged;

  const dispatch = useDispatch();

  const saveConfigs = useCallback(() => {
    if (!configs) return;

    analytic.trackVideoAppearanceChanged(configs);
    dispatch(
      saveVideoConfigs({
        hostId,
        configs: {
          ...configs,
          ...(playerColor && { playerColor })
        }
      })
    );

    wasPosterChanged && savePoster();
  }, [configs, playerColor, wasPosterChanged]);

  const saveLanding = useCallback(() => {
    if (!landing) return;

    const { parameters, template, slug, hostId } = landing;

    const analiticParams = {
      CTA: !parameters?.cta?.disabled,
      ...(parameters?.meta?.heading && { Headline: parameters?.meta?.heading }),
      ...(parameters?.meta?.bodyText && {
        BodyText: parameters?.meta?.bodyText
      }),
      ...(parameters?.cta?.buttonText && {
        'CTA text': parameters?.cta?.buttonText
      }),
      ...(parameters?.cta?.buttonLink && {
        'CTA URL': parameters?.cta?.buttonLink
      }),
      ...(parameters?.track?.googleTrackId && {
        'Google Tracking': !!parameters?.track?.googleTrackId
      })
    };
    analytic.trackSaveLandingSettings(analiticParams);

    dispatch(
      landingsActions.saveLanding({
        slug,
        hostId,
        template,
        parameters,
        public: landing.public
      })
    );
  }, [landing]);

  const savePoster = useCallback(() => {
    if (!poster || !poster.file) return;

    const defaultPosterExtension = 'jpg';

    const { extension = defaultPosterExtension, file } = poster;

    dispatch(uploadCustomPoster({ file, extension, hostId }));
  }, [poster]);

  const savePassword = useCallback(() => {
    dispatch(
      hostedVideoActions.saveVideoPassword({
        hostId,
        security: password
      })
    );
  }, [password]);

  const discardPoster = useCallback(() => dispatch(discardPosterChanges()), []);

  const discardPassword = useCallback(() => dispatch(discardPasswordChanges()), []);

  const discardConfigs = useCallback(() => {
    dispatch(discardConfigsChanges());
    dispatch(discardPlayerColorChanges());
    wasPosterChanged && discardPoster();
  }, []);

  const discardLanding = useCallback(() => dispatch(discardLandingChanges()), []);

  useEffect(() => {
    const canStartPending = !isAppearancePending && (isConfigsPending || isPosterPending);

    const canStopPending = !isConfigsPending && !wasPosterUpdated;

    canStartPending && setAppearancePending(true);
    canStopPending && setAppearancePending(false);
  }, [isConfigsPending, isPosterPending, isPosterReady, wasPosterUpdated]);

  const handlersMap: {
    [key in ChangedData]: ActionHandlers;
  } = {
    poster: {
      save: savePoster,
      discard: discardPoster,
      isPending: isAppearancePending
    },
    configs: {
      save: saveConfigs,
      discard: discardConfigs,
      isPending: isAppearancePending
    },
    landing: {
      save: saveLanding,
      discard: discardLanding,
      isPending: isLandingPending
    },
    password: {
      save: savePassword,
      discard: discardPassword,
      isPending: isPasswordPending
    }
  };

  const getHandlers = (): ActionHandlers => {
    switch (true) {
      case isWebinar && wasConfigsChanged && wasLandingChanged:
        return {
          save: () => {
            handlersMap['configs'].save();
            handlersMap['landing'].save();
          },
          discard: () => {
            handlersMap['configs'].discard();
            handlersMap['landing'].discard();
          },
          isPending: handlersMap['configs'].isPending || handlersMap['landing'].isPending
        };

      case wasConfigsChanged:
        return handlersMap['configs'];

      case wasPosterUpdated:
        return handlersMap['poster'];

      case wasLandingChanged:
        return handlersMap['landing'];

      case wasPasswordChanged:
        return handlersMap['password'];

      default:
        return {
          save: noop,
          discard: noop,
          isPending: false
        };
    }
  };

  return [wasChanged, getHandlers] as const;
};

export default useHandlers;
